import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import extractText from 'flarum/common/utils/extractText';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Placeholder from 'flarum/common/components/Placeholder';
// Was the plain migrated discussion (phpBB topic 6519 + ID_OFFSET) - that
// phpBB forum ("Régles") is no longer imported at all (see
// migration/03_discussions_posts.php's own EXCLUDED_FORUM_IDS), replaced
// by a hand-authored page at this fixed low id instead (see
// migration/static_pages.php - kept in sync by hand here, this component
// has no access to that migration-only file).
const RULES_DISCUSSION_ID = '9';

// app.modal.show()'s own first parameter type (UnsafeModalClass |
// AsyncModalClass) isn't exported from flarum/core - extracted through
// Parameters<> instead of naming it directly, so this stays correct even
// if that internal type is ever renamed.

/**
 * Shown instead of SignUpModal the first time it's about to open (see
 * addRegistrationTerms.js) - "Règlement du forum" with accept/decline
 * buttons, matching champis.net's own registration flow
 * (ucp.php?mode=register). A standalone Modal rather than SignUpModal
 * itself showing different content depending on internal state: simpler to
 * reason about (a completely fresh, un-overridden component - no
 * dependency on correctly toggling render output on an already-mounted,
 * already-patched instance) and no less faithful to the two-step reference
 * flow. Declining just closes; accepting opens a genuinely fresh
 * SignUpModal (the same class/attrs the caller originally asked for) via
 * the normal app.modal.show() path - no custom logic involved in actually
 * displaying the form.
 *
 * The text is the live first post of discussion 9 ("Règlement du
 * forum", see RULES_DISCUSSION_ID) rather than a copy baked into this
 * extension - editing that discussion like any other post is now the only
 * way to update these terms, instead of also having to touch this
 * extension's code to keep a second copy in sync. That discussion's tags
 * (Rédactions, Régles) are both unrestricted, so it resolves for guests the
 * same way any other public discussion would - no special-cased fetch
 * needed.
 */
export default class RegistrationTermsModal extends Modal {
  reachedEnd = false;
  post = null;
  error = false;
  oninit(vnode) {
    super.oninit(vnode);

    // Gates the accept button below - starts false, flips permanently true
    // once checkScroll() sees the text scrolled (or already fitting
    // without a scrollbar at all - see that method) to its bottom.
    this.reachedEnd = false;
    this.post = null;
    this.error = false;
    this.load();
  }
  load() {
    app.store.find('discussions', RULES_DISCUSSION_ID, {
      include: 'firstPost'
    }).then(discussion => {
      this.post = discussion.firstPost() || null;
      m.redraw();
    }).catch(() => {
      this.error = true;
      m.redraw();
    });
  }
  className() {
    return 'Modal--large RegistrationTermsGate';
  }
  title() {
    return app.translator.trans('champis-users.forum.registration_terms.modal_title');
  }
  content() {
    return [m("div", {
      className: "Modal-body"
    }, m("div", {
      className: "RegistrationTermsGate-text",
      tabindex: "0",
      role: "region",
      "aria-label": extractText(app.translator.trans('champis-users.forum.registration_terms.modal_title')),
      oncreate: vnode => this.checkScroll(vnode.dom),
      onupdate: vnode => this.checkScroll(vnode.dom),
      onscroll: e => this.checkScroll(e.target)
    }, this.error ? m(Placeholder, {
      text: app.translator.trans('champis-users.forum.registration_terms.error')
    }) : this.post ? m.trust(this.post.contentHtml() || '') : m(LoadingIndicator, null)), this.post && !this.reachedEnd && m("p", {
      className: "RegistrationTermsGate-hint"
    }, app.translator.trans('champis-users.forum.registration_terms.scroll_hint'))), m("div", {
      className: "Modal-footer RegistrationTermsGate-footer"
    }, m(Button, {
      className: "Button",
      onclick: () => app.modal.close()
    }, app.translator.trans('champis-users.forum.registration_terms.decline_button')), m(Button, {
      className: "Button Button--primary",
      disabled: !this.reachedEnd,
      onclick: () => this.accept()
    }, app.translator.trans('champis-users.forum.registration_terms.accept_button')))];
  }

  // 4px of tolerance for sub-pixel scroll rounding (some browsers/zoom
  // levels never report scrollTop + clientHeight === scrollHeight exactly
  // at the true bottom). Runs on oncreate/onupdate as well as onscroll:
  // the text arrives asynchronously (see load()), so the first render has
  // nothing to measure yet - onupdate re-checks once the fetched content
  // actually lands (also covers it fitting without a scrollbar at all, a
  // short enough post in a tall viewport).
  checkScroll(el) {
    if (this.reachedEnd) return;

    // Nothing real to measure yet while the post is still loading (just
    // the LoadingIndicator, which trivially "fits") - checking would
    // wrongly latch reachedEnd before the actual text has even rendered.
    if (!this.post) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 4) {
      this.reachedEnd = true;
      m.redraw();
    }
  }
  accept() {
    app.modal.show(this.attrs.signUpModal, {
      ...this.attrs.signUpAttrs,
      champisTermsAccepted: true
    });
  }
}
flarum.reg.add('champis-users', 'forum/components/RegistrationTermsModal', RegistrationTermsModal);